// Organization admin console: usage at a glance and the audit log. Admins and owners only (the API enforces it).
import { useState } from "react";
import { Link } from "react-router";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { formatSize, formatShort } from "../../lib/chat";
import { Avatar, Card, Spinner, cx } from "../../components/ui";
import { Icon, type IconName } from "../../components/icons";
import { atLeast, useOrg } from "../org";

type Overview = {
  members: { active: number; byRole: Record<"owner" | "admin" | "member" | "guest", number>; suspended: number; pendingInvitations: number };
  activeLast7Days: number; messagesLast7Days: number; messagesPerDay: { day: string; count: number }[];
  callsLast30Days: { count: number; minutes: number }; meetingsLast30Days: { count: number; minutes: number };
  storage: { bytes: number; files: number }; teams: number; channels: number;
};
type AuditPage = { entries: { id: string; at: string; action: string; actor: { id: string; name: string } | null; summary: string }[]; hasMore: boolean };

export function AdminPage() {
  const { org, role } = useOrg();
  const data = useQuery({ queryKey: ["admin-overview", org.slug], queryFn: () => api<Overview>(`/orgs/${org.slug}/admin/overview`), enabled: atLeast(role, "admin") });
  const audit = useInfiniteQuery({
    queryKey: ["admin-audit", org.slug],
    queryFn: ({ pageParam }) => api<AuditPage>(`/orgs/${org.slug}/admin/audit?limit=30${pageParam ? `&before=${encodeURIComponent(pageParam)}` : ""}`),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.hasMore ? last.entries.at(-1)?.at : undefined),
    enabled: atLeast(role, "admin"),
  });
  if (!atLeast(role, "admin")) return <div className="mx-auto max-w-3xl"><Card><p className="text-sm text-slate-600">Only admins and owners can see this page.</p></Card></div>;
  const o = data.data;
  const max = Math.max(1, ...(o?.messagesPerDay.map((d) => d.count) ?? [1]));
  // A hover tooltip is the only way the chart gave up its numbers, and a finger never hovers: a tap names the day too.
  const [pickedDay, setPickedDay] = useState<string | null>(null);
  const entries = audit.data?.pages.flatMap((p) => p.entries) ?? [];

  return (
    <div className="mx-auto max-w-5xl space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><h1 className="text-2xl font-semibold tracking-tight">Admin</h1><p className="mt-1 text-sm text-slate-600 dark:text-slate-400">How {org.name} is using the workspace, and who changed what.</p></div>
        <div className="flex flex-wrap gap-2 text-sm">
          <Link to={`/o/${org.slug}/members`} className="rounded-lg px-3 py-2 font-medium text-brand-700 hover:bg-brand-50 dark:text-brand-100 dark:hover:bg-brand-700/30">Manage people</Link>
          <Link to={`/o/${org.slug}/billing`} className="rounded-lg px-3 py-2 font-medium text-brand-700 hover:bg-brand-50 dark:text-brand-100 dark:hover:bg-brand-700/30">Billing</Link>
          <Link to={`/o/${org.slug}/settings`} className="rounded-lg px-3 py-2 font-medium text-brand-700 hover:bg-brand-50 dark:text-brand-100 dark:hover:bg-brand-700/30">Organization settings</Link>
        </div>
      </div>

      {!o ? <div className="flex justify-center p-8 text-brand-600"><Spinner /></div> : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat icon="users" label="Members" value={o.members.active} detail={`${plural(o.members.byRole.owner + o.members.byRole.admin, "admin")} · ${plural(o.members.byRole.guest, "guest")}${o.members.suspended ? ` · ${o.members.suspended} suspended` : ""}`} />
            <Stat icon="home" label="Active (7 days)" value={o.activeLast7Days} detail={o.members.active ? `${Math.round((o.activeLast7Days / o.members.active) * 100)}% of members` : ""} />
            <Stat icon="chat" label="Messages (7 days)" value={o.messagesLast7Days} detail={`${o.teams} teams · ${o.channels} channels`} />
            <Stat icon="link" label="Invitations waiting" value={o.members.pendingInvitations} detail="Not accepted yet" />
            <Stat icon="phone" label="Calls (30 days)" value={o.callsLast30Days.count} detail={`${o.callsLast30Days.minutes} minutes`} />
            <Stat icon="video" label="Meetings (30 days)" value={o.meetingsLast30Days.count} detail={`${o.meetingsLast30Days.minutes} minutes`} />
            <Stat icon="file" label="Files stored" value={o.storage.files} detail={formatSize(o.storage.bytes)} />
          </div>

          <Card>
            <h2 className="font-semibold">Messages per day</h2>
            <p className="text-sm text-slate-500">The last 14 days.</p>
            <div className="mt-6 flex h-40 items-end gap-1.5" role="img" aria-label={`Messages per day: ${o.messagesPerDay.map((d) => `${d.day} ${d.count}`).join(", ")}`}>
              {o.messagesPerDay.map((d) => (
                <button
                  type="button" key={d.day} aria-label={`${formatShort(d.day)}: ${d.count}`}
                  onClick={() => setPickedDay((p) => (p === d.day ? null : d.day))}
                  className="group relative flex h-full flex-1 flex-col items-center justify-end"
                >
                  <span className={cx("pointer-events-none absolute -top-6 whitespace-nowrap rounded bg-slate-900 px-1.5 py-0.5 text-xs text-white group-hover:block", pickedDay === d.day ? "block" : "hidden")}>{d.count}</span>
                  <div className="w-full rounded-t bg-brand-500/80 group-hover:bg-brand-600" style={{ height: `${Math.max(2, (d.count / max) * 100)}%` }} />
                </button>
              ))}
            </div>
            <div className="mt-2 flex justify-between text-xs text-slate-400"><span>{formatDayLabel(o.messagesPerDay[0]?.day)}</span><span>Today</span></div>
          </Card>
        </>
      )}

      <section>
        <h2 className="mb-3 text-lg font-semibold">Audit log</h2>
        <Card className="p-0">
          {audit.isLoading && <div className="flex justify-center p-8 text-brand-600"><Spinner /></div>}
          {audit.data && entries.length === 0 && <p className="p-6 text-center text-sm text-slate-500">Nothing yet.</p>}
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {entries.map((e) => (
              <li key={e.id} className="flex items-center gap-3 px-5 py-3">
                <Avatar name={e.actor?.name ?? "?"} size="sm" />
                <p className="min-w-0 flex-1 text-sm"><span className="font-medium">{e.actor?.name ?? "Someone"}</span> {e.summary}</p>
                <time dateTime={e.at} title={new Date(e.at).toLocaleString()} className="shrink-0 text-xs text-slate-400">{formatShort(e.at)}</time>
              </li>
            ))}
          </ul>
          {audit.hasNextPage && (
            <div className="border-t border-slate-100 p-3 text-center dark:border-slate-800">
              <button type="button" onClick={() => void audit.fetchNextPage()} disabled={audit.isFetchingNextPage} className="text-sm font-medium text-brand-600 hover:underline">{audit.isFetchingNextPage ? "Loading…" : "Load more"}</button>
            </div>
          )}
        </Card>
      </section>
    </div>
  );
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;
const formatDayLabel = (day?: string) => (day ? new Date(`${day}T12:00:00`).toLocaleDateString([], { day: "numeric", month: "short" }) : "");

function Stat({ icon, label, value, detail }: { icon: IconName; label: string; value: number; detail: string }) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 text-sm text-slate-500"><Icon name={icon} className="h-4 w-4" />{label}</div>
      <div className="mt-2 text-3xl font-semibold tabular-nums">{value.toLocaleString()}</div>
      <div className="mt-1 truncate text-xs text-slate-500">{detail}</div>
    </Card>
  );
}
